import { z } from 'zod';
import { EMAIL_LOCAL_PART_PATTERN } from '../constants/email-sender';
import { LICENSE_CATEGORY_GROUP_KEYS } from '../constants/license-category';
import { MATCHMAKER_SOURCE_TYPES } from '../constants/license-source-type';
import { OrderStatus } from '../enums/order-status.enum';

/**
 * Schema for Access Logging settings
 */
export const AccessLoggingSchema = z.object({
	verboseLogging: z
		.boolean()
		.default(false)
		.describe('Enable verbose request/response logging'),
});

/**
 * Schema for the System AI Agent (Sales Helper) availability toggle.
 *
 * `enabled` controls whether the code-defined system agent (currently
 * `sales-helper`) is exposed in the CRM chat widget. Opt-in: defaults to
 * `false` so tenants must explicitly turn the widget on. The setting writer
 * also flips `is_chat_widget_default` on the matching `conversation_agent`
 * row so the widget endpoint can read DB state directly.
 */
export const SystemAgentEnabledSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Enable the system AI agent (Sales Helper) for the CRM chat widget. When disabled, the widget is hidden for all users in this tenant.',
		),
});

/**
 * Schema for AI Agent Credits settings
 */
export const ConversationAgentCreditsSchema = z.object({
	voiceMinuteCredits: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(100_000_000, 'Maximum is 100 million minutes')
		.default(100)
		.describe('Monthly voice minute allocation'),

	llmTokenCredits: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(100_000_000_000, 'Maximum is 100 billion tokens')
		.default(1_000_000)
		.describe('Monthly LLM token allocation'),
});

/**
 * Schema for RingCentral integration settings
 */
export const RingCentralSettingsSchema = z.object({
	callMode: z
		.enum(['ringout', 'callout'])
		.default('ringout')
		.describe(
			'Call mode: ringout (calls your phone first) or callout (browser-based)',
		),
});

/**
 * Schema for AI Agent Throttle Concurrency settings
 */
export const ThrottleConcurrencySchema = z.object({
	inbound: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent inbound voice and web sessions'),

	outbound: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent outbound voice calls'),

	chat: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent chat sessions'),
});

/**
 * Schema for the per-verified-domain default outbound sender.
 *
 * Keyed by the domain name exactly as it is registered in the login-DB
 * `_domain` table (lower-cased at read time so casing drift can't hide an
 * entry). Only domains the tenant has actually verified are ever consulted,
 * so an orphaned key is inert rather than dangerous.
 *
 * `localPart` is the part before the `@`; the historical `noreply` is used
 * when a domain has no entry. `fromName` is the optional display name
 * applied when the message itself does not carry one.
 */
export const EmailDomainSendersSchema = z.object({
	domains: z
		.record(
			z.string(),
			z.object({
				localPart: z
					.string()
					.regex(
						EMAIL_LOCAL_PART_PATTERN,
						'Local part may contain only letters, numbers, dot, underscore, plus and hyphen (max 64)',
					)
					.describe('Part before the @ — e.g. "sales" for sales@acme.com'),
				fromName: z
					.string()
					.max(150)
					.optional()
					.describe('Optional display name shown to recipients'),
			}),
		)
		.default({})
		.describe('Per-domain default sender, keyed by verified domain name'),
});

/**
 * Schema for the tenant-level lead registration notification recipient.
 * String form (single email) is the simple case; comma-separated list
 * works for multiple recipients without needing array gymnastics in the
 * admin UI. Empty string treated by callers as "not configured" so
 * unset tenants fall through to the system default / log warning path.
 */
export const LeadRegistrationEmailSchema = z
	.string()
	.default('')
	.describe(
		'Email recipient(s) for new lead registrations. Single address or comma-separated list. Empty = not configured.',
	);

/**
 * Schema for the tenant-level ERP integration mode.
 *
 * Two modes:
 *  - `workd_as_erp` (default) — Workd IS the ERP for this tenant. Orders
 *    that land in APPROVED status stay inside Workd, so the user can
 *    cancel them (and the trip-cancel flow can release / cancel them
 *    with auth-void).
 *  - `third_party` — Workd integrates with a downstream ERP (NetSuite,
 *    SAP, Acumatica, QuickBooks-Enterprise, etc.). Once an order is
 *    APPROVED it gets pushed out and we no longer control its
 *    lifecycle, so cancel-after-approval is rejected. (Note: as of
 *    2026-05-22 no actual third-party-ERP sync code exists in this
 *    codebase — the restriction has been theoretical. Tenants who add
 *    an ERP integration later flip this setting and wire their sync.)
 *
 * Cancel-order gate uses this setting at apps/crm-api/src/modules/
 * orders/orders.service.ts:cancelOrder — when `mode === 'workd_as_erp'`
 * the cancellable-status list includes APPROVED.
 */
export const ErpIntegrationSchema = z.object({
	mode: z
		.enum(['workd_as_erp', 'third_party'])
		.default('workd_as_erp')
		.describe(
			'workd_as_erp: orders remain inside Workd and stay cancellable after approval. third_party: orders sync to an external ERP and become non-cancellable post-approval.',
		),
});

/**
 * Schema for Pharmacy License Compliance settings.
 *
 * `enabled` controls whether the tenant uses pharmacy compliance features.
 *
 * Match confirmation defaults to human-only: every registry match lands as
 * `pending_review` for explicit confirmation on the License Matchmaker screen.
 * PD-14391 makes that opt-out-able per tenant through the `autoConfirm*` keys
 * below (all default to the human-only behavior). Old `autoApproveThreshold`
 * and `notifyOnAutoPair` keys may still appear in legacy instance_setting rows
 * but are no longer read.
 */
export const PharmacyComplianceSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Master switch for the licensing system. When OFF (default), every licensing background job (registry harvest, matchmaker scans, registry-status detection, mark-expired sweep, daily license notifications, compliance-stats refresh) is skipped for this tenant and order-validation skips license-status enforcement. Turn ON only after the tenant is set up to use pharmacy compliance (NPI, DEA verification, state board registry sync, enhanced order blocking).',
		),
	requireVerifiedLicenseAtCheckout: z
		.boolean()
		.default(false)
		.describe(
			'Customer-facing checkout enforcement (PD-14368). When OFF (default), the order-checkout license-good-standing surfaces are suppressed: the cart sidebar shows no license indicator and order submission is never blocked for a client lacking a confirmed/verified license — even while the rest of the licensing subsystem (matchmaker, dashboard, registry harvest) keeps running. Turn ON per-tenant only once matchmaking has been underway and licensing is being managed the new way, so we do not nag customers with negative-sentiment checkout warnings during ramp-up. Has effect only when `enabled` is also true.',
		),
	licenseCategories: z
		.array(z.string())
		.default(['PHARMACY'])
		.describe(
			'License categories to monitor (e.g. PHARMACY, PHARMACIST, TECHNICIAN)',
		),
	// PD-14842: per-tenant matchmaking toggles. Stored as DISABLED sets (default
	// empty = everything matched) so a newly-added source/category stays enabled
	// for existing tenants without a backfill. The matchmaker reads these to skip
	// source-specific passes and to filter the state registry slice.
	disabledMatchSourceTypes: z
		.array(z.enum(MATCHMAKER_SOURCE_TYPES))
		.default([])
		.meta({
			title: 'Disabled matchmaking sources',
			description:
				'Registry source types EXCLUDED from matchmaking for this tenant. Checked = excluded. Empty (default) = every source is matched. Example: check NPI to stop the matchmaker suggesting NPI records.',
		}),
	disabledMatchCategoryGroups: z
		.array(z.enum(LICENSE_CATEGORY_GROUP_KEYS))
		.default([])
		.meta({
			title: 'Disabled matchmaking categories',
			description:
				'License-category groups EXCLUDED from matchmaking for this tenant. Checked = excluded. Empty (default) = every category is matched. Example: check Veterinary to stop veterinary state-board matches while pharmacy state-board matching continues.',
		}),
	// PD-14391: per-tenant AUTO-CONFIRM policy. Distinct from the
	// `disabledMatch*` sets above — those decide what the matchmaker even
	// SUGGESTS; these decide which of those suggestions may be confirmed
	// without a human click. Every value here is ADDITIVE on top of the
	// unconditional Tier-1 safety floor enforced in
	// `LicenseMatchReviewService.autoConfirmHighConfidence` (exact raw license
	// number + same state + identical name + single candidate + Active
	// registry record), so no combination of these settings can make
	// auto-confirm LOOSER than that floor — only stricter.
	//
	// Polarity note: the two scope fields are DISABLED sets (deny-lists), the
	// same convention as `disabledMatchSourceTypes` /
	// `disabledMatchCategoryGroups` directly above, so the four checkbox lists
	// in the settings dialog all read "checked = excluded" rather than flipping
	// meaning halfway down the form. Default OFF is carried by
	// `autoConfirmEnabled: false` (nothing auto-confirms at all) plus
	// `autoConfirmMinConfidence: 100` (the strictest score the matcher can
	// produce), NOT by the scope sets — an allow-list would additionally be
	// inexpressible for categories, because `LICENSE_CATEGORY_GROUP_KEYS`
	// covers only VETERINARY and a pharmacy state-board match belongs to no
	// group at all.
	autoConfirmEnabled: z.boolean().default(false).meta({
		title: 'Auto-confirm matches',
		description:
			'When ON, the matchmaker may confirm a pending match without a human click. DEFAULT OFF: every match lands in Pending Confirmation for a reviewer. Even when ON, only matches that clear the confidence threshold, the source/category scope below, AND the built-in Tier-1 safety rule (exact license number, same state, identical name, exactly one candidate, Active registry record) are ever auto-confirmed.',
	}),
	autoConfirmMinConfidence: z.number().min(50).max(100).default(100).meta({
		title: 'Auto-confirm confidence threshold',
		description:
			'Minimum match confidence (0-100) required to auto-confirm. 100 (default) means only a perfect score qualifies — an exact license-number match whose client and registry names are identical. Lowering this can never admit a match that fails the Tier-1 safety rule; it only narrows further. Ignored while Auto-confirm matches is OFF.',
	}),
	autoConfirmDisabledSourceTypes: z
		.array(z.enum(MATCHMAKER_SOURCE_TYPES))
		.default([])
		.meta({
			title: 'Auto-confirm: excluded sources',
			description:
				'Registry source types that may NEVER auto-confirm for this tenant, even at full confidence. Checked = excluded. Empty (default) = every source that clears the other gates may auto-confirm. Matches excluded here still appear as suggestions for human review.',
		}),
	autoConfirmDisabledCategoryGroups: z
		.array(z.enum(LICENSE_CATEGORY_GROUP_KEYS))
		.default([])
		.meta({
			title: 'Auto-confirm: excluded categories',
			description:
				'License-category groups that may NEVER auto-confirm for this tenant, even at full confidence. Checked = excluded. Empty (default) = every category that clears the other gates may auto-confirm. Matches excluded here still appear as suggestions for human review.',
		}),
	notifyOnExpiring: z
		.boolean()
		.default(false)
		.describe(
			'Send notifications when monitored licenses are nearing expiration. Defaults off — must be opted in per tenant.',
		),
	notifyDaysBeforeExpiry: z
		.number()
		.min(7)
		.max(180)
		.default(30)
		.describe(
			'Days before expiration to trigger an expiring-soon notification (7-180)',
		),
	notifyOnStatusChange: z
		.boolean()
		.default(false)
		.describe(
			'Send notifications when a license status changes (e.g. Active → Suspended). Defaults off — must be opted in per tenant.',
		),
	notifyOnDisciplinaryAction: z
		.boolean()
		.default(false)
		.describe(
			'Send notifications when a disciplinary action is detected on a license. Defaults off — must be opted in per tenant.',
		),
});

/**
 * Schema for the order-day license check gate (PD-14806/PD-14807).
 *
 * A separate opt-in from the master `licensing.pharmacy_compliance` switch: a
 * license can be revoked/suspended/disciplined any day, so this re-verifies every
 * license stamped on an order AS OF SHIP DAY before the order reaches the ERP.
 * DEFAULT OFF because most licenses are not yet human-confirmed/matched, so gating
 * today would hold nearly every order — it turns on per tenant once confirmations
 * accumulate. The flag flip and the gate code must deploy together.
 */
export const OrderLicenseCheckSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'When ON, an order entering checkout is held in "Pending License Check" until every license stamped on it is verified good as of today (then it takes the tenant’s normal submission status and flows to the ERP), or a check fails/times out (then it is held for Compliance Review and the compliance team is emailed). DEFAULT OFF: enable per tenant only once matched licenses have accumulated, or nearly every order would be held. Has effect only when `licensing.pharmacy_compliance.enabled` is also true.',
		),
	timeoutMinutes: z
		.number()
		.min(1)
		.max(60)
		.default(10)
		.describe(
			'How long an order may sit in "Pending License Check" before the timeout watchdog routes it to Compliance Review with the last-known status (guarantees the 10–15 minute expectation and makes a verification-machine outage degrade safely to human review, never a silent hang).',
		),
});

/**
 * Schema for the tenant-level "Pharmacy Sales" feature flag.
 *
 * Distinct from `PharmacyComplianceSchema` — that one is a per-tenant
 * licensing-subsystem switch (registry harvest, matchmaker, order
 * blocking). `PharmacySalesSchema.enabled` is the higher-level question
 * "does this tenant sell to pharmacy clients?" — when true the SPA shows
 * pharmacy-specific UI (NPI / NCPDP fields on client, pharmacy labeling,
 * future pharmacy-only features). Replaces the legacy `med-mover-feature`
 * `_preference` row in v2 instances.
 */
export const PharmacySalesSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'When ON, the tenant sells to pharmacy clients and the CRM exposes pharmacy-specific UI (NPI / NCPDP fields on the client editor, pharmacy labeling, etc.). Independent of licensing.pharmacy_compliance — a tenant can sell to pharmacies without subscribing to the license-monitoring subsystem.',
		),
});

/**
 * PD-15423 — cross-account delivery. One client sometimes orders and pays
 * for product delivered to ANOTHER client's site (e.g. a caterer running an
 * event at another client's venue) with no parent/child relationship between
 * them. When enabled, CRM order entry lets a salesperson pick another client
 * (and one of that client's addresses) as the order's delivery destination;
 * the order and invoice stay on the ordering client.
 */
export const CrossAccountDeliverySchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			"When ON, CRM order entry offers 'Deliver to another customer': the order ships to a different client's address while the order and invoice remain on the ordering client. Off by default — enable only for tenants whose clients order for delivery to each other's sites.",
		),
});

export const UrlSettingsSchema = z.object({
	crm: z.string().describe('The URL of the CRM application'),
});

/**
 * Shared schema for PDF document settings (order and quote)
 *
 * `title` metadata is the human label the config-web SettingFormDialog
 * renders for each field (falling back to the raw field name when
 * absent). `.meta({ title, description })` replaces `.describe(...)` —
 * describe is just sugar for `.meta({ description })` in Zod 4, so the
 * description behavior is unchanged.
 */
export const PdfSettingsSchema = z.object({
	senderName: z.string().nullable().optional().meta({
		title: 'Sender Name',
		description:
			'Override sender name (default: theme company name). Set null to omit.',
	}),
	senderEmail: z.string().nullable().optional().meta({
		title: 'Sender Email',
		description:
			'Override sender email (default: theme company email). Set null to omit.',
	}),
	senderAddressLines: z.array(z.string()).optional().meta({
		title: 'Sender Address Lines',
		description: 'Address lines shown under sender name',
	}),
	// PD-15298 — the seller's phone. Added here rather than as a new setting so
	// the sender identity (name / email / address / phone) stays in one place
	// and the printed delivery receipt cannot drift from the invoice PDF, which
	// already reads its sender block from this schema. Omitted entirely — label
	// and value — when unset, so no empty row appears on a template.
	senderPhone: z.string().nullable().optional().meta({
		title: 'Sender Phone',
		description:
			'Phone shown under the sender address. Leave blank to omit it entirely.',
	}),

	headerMessage: z.string().optional().meta({
		title: 'Header Message',
		description: 'HTML message above line items',
	}),
	footerMessage: z.string().optional().meta({
		title: 'Footer Message',
		description: 'HTML message below the summary',
	}),

	showSender: z.boolean().default(true).meta({
		title: 'Show Sender',
		description: 'Show the Sender section',
	}),
	showDiscount: z.boolean().default(true).meta({
		title: 'Show Discount',
		description: 'Show the Discount row',
	}),
	showShipping: z.boolean().default(true).meta({
		title: 'Show Shipping',
		description: 'Show the Shipping row',
	}),
});

/**
 * PD-15298 — settings for the printed delivery receipt.
 *
 * Separate from the PDF schemas on purpose: a thermal receipt is a different
 * document with different constraints (42 columns, no HTML, no images beyond
 * the logo), so folding its copy into `pdf.invoice` would invite HTML being
 * pasted into a field that can only ever print plain text.
 */
export const ReceiptSettingsSchema = z.object({
	thankYouMessage: z.string().default('Thank you for your business!').meta({
		title: 'Thank-you Message',
		description:
			'Printed, centred, at the bottom of every delivery receipt. Plain text only — the receipt is 42 characters wide, so anything longer wraps. Leave blank to print nothing.',
	}),
});

/**
 * Invoice PDF extends with invoice-specific toggles
 */
export const InvoicePdfSettingsSchema = PdfSettingsSchema.extend({
	showDetailsBox: z.boolean().default(true).meta({
		title: 'Show Details Box',
		description: 'Show the Details box (PO#, terms, due date, credit)',
	}),
	// PD-14983 — the remittance block always states the amount due and who to
	// pay; this is the tenant's chance to add the specifics (remit-to address,
	// ACH/wire details, a portal link). Empty by default so nothing invented
	// appears on an invoice until a tenant fills it in.
	remittanceInstructions: z.string().default('').meta({
		title: 'Remittance Instructions',
		description:
			'Payment instructions printed in the Remittance block on invoices — for example a remit-to address, ACH details, or a link to pay online.',
	}),
});

/**
 * Schema for the "Powered by X" attribution footer rendered across CRM
 * web, mobile (Capacitor), and email PDFs.
 *
 * 2026-05-23 — added so tenants can decide whether and how to attribute
 * the underlying platform. Default-on per Workd policy: every tenant
 * shows "Powered by Workd" unless they explicitly opt out, so the
 * platform gets visible credit across all the customer-facing surfaces.
 *
 * Per-storefront override lives in `PortalSiteSettings.brandAttribution`
 * (deferred to a follow-on pass — see `docs/claude/motor-city-defects/
 * branding-plan.md` for the storefront-override design).
 */
/**
 * Schema for the "Sanitize Workd users" tenant setting.
 *
 * When `enabled` is true (default for customer tenants), backend
 * user-LIST surfaces (Drivers list, Lead Eligibility, Activity Logs,
 * Login History) filter out Workd / Developrise staff for non-Workd
 * callers. The intent is to prevent a tenant admin from accidentally
 * picking a Workd staffer for production work when we happen to be
 * inside their tenant for testing/config.
 *
 * When `enabled` is false (set on internal tenants like `workd-sales`
 * where Workd staff ARE the real users), no sanitization happens —
 * Workd users are visible everywhere because they're the actual
 * operational identities.
 *
 * Default `true` is the conservative choice: a new customer tenant
 * gets sanitized automatically; an internal tenant has to opt out
 * explicitly. Only applies to user-LIST surfaces — assigned-entity
 * surfaces (route default driver, trip driver, client salesperson,
 * creator / modifier audit) always show the truth regardless of this
 * setting. See `libs/shared-server/src/utils/workd-user-filter.ts`
 * for the helper and CLAUDE.md "Workd User Visibility" for policy.
 */
export const SanitizeWorkdUsersSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'When ON (default), filter Workd/Developrise staff out of user-list surfaces (Drivers list, Lead Eligibility, Activity Logs, Login History) for non-Workd callers. Turn OFF for internal tenants (workd-sales etc.) where Workd staff are the real operational users.',
		),
});

/**
 * Governor for record imports (PD-12353). Workd pays the geocoding bill for
 * imported addresses (tenants do not), so the cap is a Workd-held dial:
 * lower it for a tenant that overuses imports rather than warning uploaders
 * who have no incentive to care. Unseeded tenants fall back to the code
 * default in the import service.
 */
export const ImportMaxRowsSchema = z.object({
	maxRows: z
		.number()
		.int()
		.positive()
		.default(10000)
		.describe(
			'Maximum data rows a single record-import run may contain. Files with more data rows are rejected at upload.',
		),
});

/**
 * Schema for the account-funds DEPOSIT switch (PD-14922).
 *
 * Splits customer account funds into the two halves that were
 * previously all-or-nothing behind the 'Account Funds' supported
 * payment method type:
 *
 *   - SPENDING existing funds — available whenever the method type is
 *     configured. NOT governed by this setting.
 *   - DEPOSITING new money (the `onAccountAmount` portion of a payment)
 *     — governed by this setting.
 *
 * The split exists because an ERP can be unable to represent a deposit
 * while handling spending fine. HF Foods' Workday attaches a card
 * processing fee to an invoice as an "invoice adjustment"; a payment
 * placed on account has no invoice to attach it to, so the fee has
 * nowhere to land and the transaction cannot be applied. Turning the
 * payment method type off entirely would also stop customers spending
 * the balance they already hold, which has to keep working.
 *
 * Defaults to true so every existing tenant keeps today's behavior; a
 * tenant whose ERP cannot record a deposit opts out explicitly.
 */
export const AllowAccountFundsDepositSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'When ON (default), customers may add money to their account funds balance (the "Apply to Customer Account" amount on Make Payment). Turn OFF for tenants whose ERP cannot record an on-account deposit. Spending existing account funds is unaffected either way.',
		),
});

/**
 * Schema for paying invoices with credit memo balances (PD-15441).
 *
 * When enabled, open credit memos (RMAs) appear alongside open invoices on
 * the Make a Payment screens (CRM and customer portal) as credit rows. The
 * customer applies some or all of a memo's remaining balance against the
 * invoices they are paying, and only the net difference is charged to their
 * payment method. The applications are recorded per document — +amount to
 * each invoice, -amount to each credit memo — which is exactly the shape the
 * ERP payment export sends (HF Foods' Workday applies the payment to the
 * invoice and the RMA in one remittance advice).
 *
 * Defaults to false: PD-15014 deliberately removed credit memos from the
 * payable list because the ERP sync stores their outstanding balance with
 * the opposite sign and they rendered as charge-able rows. Only tenants
 * whose ERP expects credit-memo applications on payments should opt in.
 */
export const PayInvoicesWithCreditMemosSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'When ON, open credit memos (RMAs) can be applied against invoices on the Make a Payment screens, charging only the net difference. Requires an ERP integration that accepts credit-memo applications on exported payments (e.g. HF Foods Workday). OFF by default.',
		),
});

/**
 * Schema for the tenant's Twilio subaccount binding (PD-14218).
 *
 * `subAccountSid` is the Twilio subaccount (`AC…`) that owns this
 * tenant's softphone numbers. Hydrated onto TenantContext by the
 * unified-auth flow and read via `ContextService.getTwilioSubAccountSid()`
 * so authenticated rep call-control ops (coaching, recording control) and
 * phone-context-less paths (E911 address registration) re-root their Twilio
 * REST resource navigation to the subaccount instead of the master account.
 * Server-only (no `isPublic`). Empty string = tenant not yet migrated to a
 * subaccount → falls back to the Twilio master account (null-safe,
 * behavior unchanged pre-cutover).
 */
export const TwilioSubAccountSidSchema = z.object({
	subAccountSid: z
		.string()
		.default('')
		.describe(
			'Twilio subaccount SID (AC…) that owns this tenant’s softphone numbers. Set during the subaccount migration. Empty = not migrated; the runtime falls back to the Twilio master account.',
		),
});

/**
 * Schema for the master Twilio softphone on/off toggle (PD-14243).
 *
 * The explicit kill-switch the Workd team flips to enable/disable the human
 * Twilio softphone for a tenant WITHOUT deleting credentials. Only ONE input
 * to the derived gate (`getTwilioFeatureEnabled`): the softphone is truly "on"
 * only when this is true AND the tenant has a provisioned subaccount SID +
 * voice key (SSM) + ≥1 Kari's Law recipient. Written by the config-app
 * Enable-Phone flow (turns it on as part of provisioning) and the crm-api
 * kill-switch endpoint (flips it off in an emergency) — both write this same
 * key, so there is no drift. Server-only.
 */
export const TwilioFeatureEnabledSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Master on/off for the tenant human softphone. Off by default; only one input to the derived "on" gate (which also needs a subaccount + voice key + Kari\'s Law recipients).',
		),
});

/**
 * Schema for the Item Intelligence engine tuning knobs.
 *
 * Drives the nightly "Frequently Purchased Together" association
 * calculation (item-purchased-together queue handler). The defaults
 * match the values that were previously hardcoded in the handler, so
 * tenants without a setting row keep the historical behavior. A
 * knowledgeable admin can tune these per tenant from Settings → Item
 * Intelligence; changes take effect on the next nightly recalculation.
 */
export const ItemIntelligenceSchema = z.object({
	lookbackMonths: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1 month')
		.max(60, 'Maximum is 60 months')
		.default(12)
		.describe(
			'How many months of invoice history to analyze when computing purchased-together associations',
		),
	topN: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(25)
		.describe(
			'Maximum number of associated items to keep per source item (ranked by cosine similarity)',
		),
	minCoOccurrence: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(3)
		.describe(
			'Minimum number of invoices two items must appear on together before the pair is considered at all',
		),
	recommendationTopN: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(200, 'Maximum is 200')
		.default(25)
		.describe(
			'How many recommended items to precompute and store per account (the account recommender)',
		),
	recommendationMinScore: z
		.number()
		.min(0, 'Minimum is 0')
		.default(0)
		.describe(
			'Minimum affinity score for a recommendation to be kept. 0 keeps the top-N regardless; raise it to prune weak suggestions',
		),
	coldStartMinItems: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Minimum is 0')
		.max(100, 'Maximum is 100')
		.default(3)
		.describe(
			'Below this many distinct purchased items, an account is treated as cold-start and gets popularity-based suggestions instead of personalized ones',
		),
	lookalikeNeighborCount: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Minimum is 0')
		.max(25, 'Maximum is 25')
		.default(5)
		.describe(
			'How many nearest lookalike accounts (by purchase-vector similarity) feed the "Popular with accounts similar to this one" recommendation layer. 0 disables the layer',
		),
});

export const BrandAttributionSchema = z.object({
	show: z
		.boolean()
		.default(true)
		.describe(
			'Render the "Powered by X" footer in the app shell. When off, the footer is hidden everywhere (CRM web, mobile, email PDFs).',
		),
	label: z
		.string()
		.max(60, 'Label must be 60 characters or fewer')
		.nullable()
		.default(null)
		.describe(
			'The brand name shown after the fixed "Powered by " prefix (e.g. "Workd", "Ice Mover"). Leave blank for the brand default. A leading "Powered by " is stripped on render, so legacy values like "Powered by Acme" still display correctly.',
		),
	logoUrl: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default(null)
		.describe(
			'Optional logo image URL shown in place of the brand name after "Powered by " (e.g. the Workd wordmark). When set, the logo replaces the text label. Leave blank to show the text label instead.',
		),
	logoUrlDark: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default(null)
		.describe(
			'Optional dark-mode logo image URL, used in place of logoUrl when the app is in dark mode (e.g. a white wordmark). Falls back to logoUrl when not set.',
		),
	linkUrl: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default('https://workd.com')
		.describe(
			'Optional click-through URL for the attribution. Leave blank to render plain text with no link.',
		),
});

/**
 * Registry mapping setting keys to their schemas and metadata
 *
 * Key format: Technical identifier stored in database
 *   - ACCESS_LOGGING (upper snake case, like env var)
 *   - conversation_agents.monthly_credits (dot notation for namespacing)
 *
 * Label format: Human-readable display name (in registry, NOT in database)
 *   - "Access Logging" - shown in UI
 *   - "AI Agent Credits" - shown in UI
 */
/**
 * Statuses a tenant may pick as the post-submission default. Deliberately a
 * SUBSET of OrderStatus — excludes transient/system statuses (unsubmitted,
 * quote, cancelled, override*) that make no sense as a submission landing
 * state. PD-14226.
 */
export const DEFAULT_SUBMISSION_STATUS_OPTIONS: ReadonlyArray<OrderStatus> = [
	OrderStatus.PENDING,
	OrderStatus.APPROVED,
	OrderStatus.APPROVAL_REQUIRED,
	OrderStatus.SHIPPED,
];

/**
 * Schema for the per-tenant default order-submission status, configurable
 * separately for CRM and ecommerce. This is the status an order lands in on
 * submission UNLESS the order-submission checks force Approval Required
 * (those always win — see orders.service.submitOrder /
 * portal-cart-submission.service.submitCart). Setting ecommerce to Approval
 * Required is how a tenant auto-holds every storefront order before it
 * reaches the ERP.
 */
export const DefaultSubmissionStatusSchema = z.object({
	crm: z
		.nativeEnum(OrderStatus)
		.refine((s) => DEFAULT_SUBMISSION_STATUS_OPTIONS.includes(s), {
			message: 'Not an allowed default submission status',
		})
		.default(OrderStatus.PENDING),
	ecommerce: z
		.nativeEnum(OrderStatus)
		.refine((s) => DEFAULT_SUBMISSION_STATUS_OPTIONS.includes(s), {
			message: 'Not an allowed default submission status',
		})
		.default(OrderStatus.PENDING),
	// PD-14234: roles (rbac_role ids) additionally allowed to approve/reject
	// orders held ONLY because the channel default status is Approval
	// Required (the DEFAULT_SETTING approval reason). Users holding the
	// blanket orders:approve / orders:reject actions are always allowed and
	// don't need to be listed here.
	crmApproverRoleIds: z.array(z.number()).optional(),
	ecommerceApproverRoleIds: z.array(z.number()).optional(),
});

/**
 * Schema for the per-tenant default State on new clients (PD-14340). When set
 * (a US state code/name), the New Client form pre-fills the State field; empty
 * means no default. Most tenants leave it empty; it's for customers who only
 * service one state.
 */
export const ClientDefaultStateSchema = z.object({
	state: z
		.string()
		.default('')
		.describe('Default State applied to new clients (empty = no default)'),
});

// PD-14335 — tenant default credit limit. Stored as an object (the dominant
// registry convention) with a single nullable `amount`. Null = no default.
export const DefaultCreditLimitSchema = z.object({
	amount: z.number().min(0, 'Cannot be negative').nullable().default(null),
});

export type DefaultCreditLimitValue = z.infer<typeof DefaultCreditLimitSchema>;

/**
 * PD-15050 — default state of the "Email invoice to customer" checkbox shown
 * when office fulfillment creates an invoice. A checkbox rather than silent
 * automation because fulfillment is also used for corrections, where emailing
 * the customer would be wrong — the person doing it knows which case they are
 * in and can always override per invoice.
 */
export const EmailInvoiceOnFulfillmentSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Pre-tick the "Email invoice to customer" checkbox when fulfillment creates an invoice. The operator can always override it per invoice.',
		),
});

// The PD-14978 payments.daily_digest setting was retired by PD-15317: the
// recipient list on Payment Settings > Daily Digest is the digest's only
// switch (recipients configured = on), so a separate enable flag had no
// meaning. Stale instance_setting rows are simply ignored.

export type EmailInvoiceOnFulfillmentValue = z.infer<
	typeof EmailInvoiceOnFulfillmentSchema
>;

/**
 * Schema for per-entity record mastering (PD-14372). For each lockable entity
 * (client/item/vendor/license, and any future entity in LOCKABLE_ENTITIES), a
 * tenant may set which system is master and override the locked-field lists.
 * A loose `z.record(string, …)` keeps it scalable — adding a new lockable
 * entity is a registry change, not a schema change. Absent keys fall back to
 * the code registry defaults (external master + historical hardcoded fields),
 * so the default `{}` reproduces today's behavior for every tenant.
 */
export const RecordMasterEnumSchema = z.enum(['external', 'workd']);
export const EntityMasteringConfigSchema = z.object({
	master: RecordMasterEnumSchema.optional().describe(
		'external (default): external system owns the record, its fields are locked when it has an externalId. workd: Workd owns the record, it stays editable.',
	),
	lockedFields: z
		.array(z.string())
		.optional()
		.describe(
			'Override of which standard fields are locked when external is master. Absent = the entity default list.',
		),
	lockedCustomFields: z
		.array(z.number())
		.optional()
		.describe('Custom-field ids locked when external is master.'),
});
export const RecordMasteringSchema = z
	.record(z.string(), EntityMasteringConfigSchema)
	.default({});

/**
 * Schema for the `_audit` / `_request` log-table retention windows
 * (PD-14818).
 *
 * Every tenant data change writes an `_audit` row and every mutating
 * HTTP request writes a `_request` row (body + headers included); with
 * no cleanup they grow unboundedly and are the platform's largest
 * uncontrolled data-retention surface. A nightly per-tenant janitor
 * (crm-api AUDIT_RETENTION_JANITOR queue) hard-deletes rows older than
 * these windows in bounded batches. Default 90 days for both; a tenant
 * needing a longer trail (e.g. regulatory) gets this setting raised in
 * the config app — there is deliberately NO CRM-side surface for it.
 *
 * `_audit` rows keep a dangling `_fk__request__id` after their
 * `_request` row ages out — by design; the audit row is the durable
 * record, the request row is diagnostic plumbing.
 *
 * Floor of 7 days guards against a fat-fingered 0/negative purging an
 * entire trail on the next nightly run.
 */
export const AuditRetentionSchema = z.object({
	requestDays: z
		.number()
		.int('Must be a whole number')
		.min(7, 'Minimum is 7 days')
		.max(3650, 'Maximum is 3650 days (10 years)')
		.default(90)
		.describe(
			'Days to keep _request rows (full HTTP request captures) before the nightly sweep deletes them',
		),
	auditDays: z
		.number()
		.int('Must be a whole number')
		.min(7, 'Minimum is 7 days')
		.max(3650, 'Maximum is 3650 days (10 years)')
		.default(90)
		.describe(
			'Days to keep _audit rows (the data-change audit trail) before the nightly sweep deletes them',
		),
});

/**
 * Schema for sensor BLE discovery name patterns (PD-15700). The Provision
 * Sensors screen matches advertised Bluetooth names against these prefixes
 * (`<prefix>_<code>`, e.g. `MotorCityICE_A7D3E7EC`). A prefix list rather
 * than a hardcode so a brand rename never needs a deploy.
 */
export const SensorDiscoveryPatternsSchema = z.object({
	patterns: z
		.array(z.string().min(1, 'Pattern cannot be empty'))
		.default(['MotorCityICE', 'IceMover'])
		.describe(
			'Advertised-name prefixes that identify sensor units during a Bluetooth scan.',
		),
});

/**
 * Schema for sensor temperature/offline alerting (PD-15417).
 */
export const SensorAlertsSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe('Master switch for sensor temperature and offline alerts.'),
	warnAboveTempF: z
		.number()
		.int('Must be a whole number')
		.nullable()
		.default(25)
		.describe(
			'Warn when a zone temperature stays ABOVE this (°F). Null disables the above-check. Per-sensor overrides win.',
		),
	warnBelowTempF: z
		.number()
		.int('Must be a whole number')
		.nullable()
		.default(null)
		.describe(
			'Warn when a zone temperature stays BELOW this (°F). Null (default) disables the below-check.',
		),
	sustainedMinutes: z
		.number()
		.int('Must be a whole number')
		.min(10)
		.default(30)
		.describe(
			'How long a temperature must stay out of spec before alerting. Devices submit roughly every 10 minutes.',
		),
	offlineAfterMinutes: z
		.number()
		.int('Must be a whole number')
		.min(30)
		.default(90)
		.describe(
			'Alert when an assigned, previously-reporting unit has been silent this long — usually a power loss.',
		),
});

export const INSTANCE_SETTINGS_REGISTRY = {
	ACCESS_LOGGING: {
		label: 'Access Logging',
		description: 'Configure access log verbosity for debugging',
		category: 'System',
		schema: AccessLoggingSchema,
		defaultValue: { verboseLogging: false },
	},
	'branding.attribution': {
		label: 'Brand Attribution Footer',
		description:
			'Controls the "Powered by X" footer rendered across CRM web, mobile, and email PDFs. Default shows "Powered by Workd". White-label tenants can override the label or hide the footer entirely.',
		category: 'Branding',
		schema: BrandAttributionSchema,
		// `isPublic: true` allows this value to flow into the
		// pre-auth public-bootstrap response so the login screen
		// can render the attribution before a user is identified.
		// Other settings (RingCentral creds, AI throttle, etc.)
		// stay server-only by default — no `isPublic` flag, no
		// exposure.
		isPublic: true,
		defaultValue: {
			show: true,
			label: null,
			logoUrl: null,
			logoUrlDark: null,
			linkUrl: 'https://workd.com',
		},
	},
	'conversation_agents.monthly_credits': {
		label: 'AI Agent Credits',
		description: 'Monthly voice minute and LLM token limits',
		category: 'AI Agents',
		schema: ConversationAgentCreditsSchema,
		defaultValue: {
			voiceMinuteCredits: 100,
			llmTokenCredits: 1_000_000,
		},
	},
	'ai_agents.system_agent_enabled': {
		label: 'System AI Agent Enabled',
		description:
			'Enable the system AI agent (Sales Helper) chat widget for this tenant',
		category: 'AI Agents',
		schema: SystemAgentEnabledSchema,
		defaultValue: { enabled: false },
	},
	'ringcentral.settings': {
		label: 'RingCentral Settings',
		description: 'Configuration for RingCentral click-to-dial integration',
		category: 'Integrations',
		schema: RingCentralSettingsSchema,
		defaultValue: { callMode: 'ringout' },
	},
	'throttle.concurrency': {
		label: 'AI Agent Throttle Concurrency',
		description: 'Max concurrent AI agent sessions per channel type',
		category: 'AI Agents',
		schema: ThrottleConcurrencySchema,
		defaultValue: {
			inbound: 10,
			outbound: 10,
			chat: 10,
		},
	},
	URL: {
		label: 'URLs',
		description: 'URLs related to this instance',
		category: 'System',
		schema: UrlSettingsSchema,
	},
	'pdf.invoice': {
		label: 'Invoice PDF',
		description:
			'Customize invoice PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: InvoicePdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDetailsBox: true,
			showDiscount: true,
			showShipping: true,
			remittanceInstructions: '',
		},
	},
	'printing.receipt': {
		label: 'Delivery Receipt',
		description:
			'Customize the receipt drivers print for customers on a Bluetooth thermal printer',
		category: 'Documents',
		schema: ReceiptSettingsSchema,
		defaultValue: { thankYouMessage: 'Thank you for your business!' },
	},
	'pdf.order': {
		label: 'Order PDF',
		description:
			'Customize order PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: PdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDiscount: true,
			showShipping: true,
		},
	},
	'pdf.quote': {
		label: 'Quote PDF',
		description:
			'Customize quote PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: PdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDiscount: true,
			showShipping: true,
		},
	},
	'erp.integration': {
		label: 'ERP Integration',
		description:
			"Whether Workd serves as this tenant's ERP or integrates with a third-party ERP. When Workd IS the ERP, orders remain cancellable after approval. When integrated with a third-party ERP, orders become non-cancellable once approved (because they've been pushed downstream).",
		category: 'Integrations',
		schema: ErpIntegrationSchema,
		defaultValue: { mode: 'workd_as_erp' },
	},
	'licensing.pharmacy_compliance': {
		label: 'Pharmacy License Compliance',
		description:
			'Enable pharmacy-specific compliance features including NPI, DEA verification, state board registry sync, and enhanced order blocking',
		category: 'Licensing',
		schema: PharmacyComplianceSchema,
		defaultValue: { enabled: false },
	},
	'licensing.order_license_check': {
		label: 'Order-Day License Check',
		description:
			'Hold each order in "Pending License Check" until every stamped license is re-verified good as of ship day, before it reaches the ERP. Default off; enable per tenant once matched licenses have accumulated. Requires Pharmacy License Compliance to be on.',
		category: 'Licensing',
		schema: OrderLicenseCheckSchema,
		defaultValue: { enabled: false, timeoutMinutes: 10 },
	},
	'features.pharmacy_sales': {
		label: 'Sells to Pharmacies',
		description:
			'Mark this tenant as one that sells to pharmacy clients. When ON, the CRM shows pharmacy-specific UI (NPI / NCPDP fields on the client editor, pharmacy labeling, future pharmacy-only features). Independent of the Pharmacy License Compliance setting.',
		category: 'Features',
		schema: PharmacySalesSchema,
		defaultValue: { enabled: false },
	},
	'items.intelligence': {
		label: 'Item Intelligence',
		description:
			'Tuning for the "Frequently Purchased Together" association engine: how many months of invoice history to analyze, how many associations to keep per item, and the minimum co-purchase count for a pair to qualify. Defaults match the built-in engine behavior.',
		category: 'Items',
		schema: ItemIntelligenceSchema,
		defaultValue: {
			lookbackMonths: 12,
			topN: 25,
			minCoOccurrence: 3,
			recommendationTopN: 25,
			recommendationMinScore: 0,
			coldStartMinItems: 3,
			lookalikeNeighborCount: 5,
		},
	},
	'email.domain_senders': {
		label: 'Default Sender Per Sending Domain',
		description:
			'Per-verified-domain default sender for outbound tenant email. For each verified sending domain, set the local part (the text before the @) and an optional display name. Outbound tenant email with no explicit from-address is sent from <localPart>@<primary domain>; domains with no entry keep the historical noreply. The display name is applied only when the message does not already carry one.',
		category: 'Email',
		schema: EmailDomainSendersSchema,
		defaultValue: { domains: {} },
	},
	'notifications.lead_registration_email': {
		label: 'Lead Registration Notification Email',
		description:
			'Email address (or comma-separated list) that receives a notification when a new self-serve customer lead is submitted via /api/portal/register. Per-portal_site overrides win over this tenant-level default.',
		category: 'Notifications',
		schema: LeadRegistrationEmailSchema,
		defaultValue: '',
	},
	'sensors.alerts': {
		label: 'Sensor Alerts',
		description:
			'Temperature and offline alerting for sensor-equipped merchandisers (PD-15417). Only units assigned to a customer location alert. Warn-above default suits ice merchandisers (they run well below freezing, so 25°F already means trouble); per-sensor overrides win. Sustained minutes translates to consecutive ~10-minute device submissions. Offline-after covers the power-loss case: a dead unit sends no warm readings, it just goes silent. Alert recipients are configured with the document copy recipients (Sensor Alerts type).',
		category: 'Distribution',
		schema: SensorAlertsSchema,
		defaultValue: {
			enabled: true,
			warnAboveTempF: 25,
			warnBelowTempF: null,
			sustainedMinutes: 30,
			offlineAfterMinutes: 90,
		},
	},
	'sensors.discovery.namePatterns': {
		label: 'Sensor Discovery Name Patterns',
		description:
			'Advertised Bluetooth name prefixes that identify merchandiser sensor units on the Provision Sensors screen (PD-15700). Units advertise as <prefix>_<code>; only devices matching one of these prefixes appear in a scan.',
		category: 'Distribution',
		schema: SensorDiscoveryPatternsSchema,
		defaultValue: { patterns: ['MotorCityICE', 'IceMover'] },
	},
	'ordering.cross_account_delivery': {
		label: 'Allow Cross-Account Delivery',
		description:
			"When ON, CRM order entry offers 'Deliver to another customer': a salesperson can pick a different client (and one of that client's addresses) as the order's delivery destination. The order and invoice stay on the ordering client; both sides get visibility (ship-to client on the order, a Deliveries Received view on the destination client). Off by default.",
		category: 'Ordering',
		schema: CrossAccountDeliverySchema,
		defaultValue: { enabled: false },
	},
	'ordering.default_submission_status': {
		label: 'Default Order Status on Submission',
		description:
			'The status an order transitions to when submitted, set separately for CRM and ecommerce. Order-submission checks (credit hold, credit limit, etc.) still override this to Approval Required when they trigger — this only sets the default for an otherwise-clean order. Set ecommerce to Approval Required to auto-hold all storefront orders before they reach the ERP.',
		category: 'Ordering',
		schema: DefaultSubmissionStatusSchema,
		defaultValue: {
			crm: OrderStatus.PENDING,
			ecommerce: OrderStatus.PENDING,
		},
	},
	'clients.default_state': {
		label: 'Default State for New Clients',
		description:
			'When set, the New Client form pre-fills the State field with this value (still editable). Empty = no default. Useful for tenants that only service accounts in one state.',
		category: 'Clients',
		schema: ClientDefaultStateSchema,
		defaultValue: { state: '' },
	},
	'finance.default_credit_limit': {
		label: 'Default Credit Limit',
		description:
			"Default credit limit prefilled into a new client's Credit Limit on the create/edit form when it is empty. Skipped for ERP-integrated clients (those with an externalId). Empty = no default.",
		category: 'Ordering',
		schema: DefaultCreditLimitSchema,
		defaultValue: { amount: null },
	},
	'invoicing.email_invoice_on_fulfillment': {
		label: 'Email Invoice on Fulfillment',
		description:
			'Pre-sets the "Email invoice to customer" checkbox shown when office fulfillment creates an invoice, so the no-trip path can send the invoice the moment it is created. The person completing fulfillment can always untick it — fulfillment is also used for corrections, where emailing the customer would be wrong.',
		category: 'Ordering',
		schema: EmailInvoiceOnFulfillmentSchema,
		defaultValue: { enabled: false },
	},
	'users.sanitize_workd_users': {
		label: 'Sanitize Workd Users in Selectors',
		description:
			"When ON (default), Workd/Developrise staff are filtered out of user-list surfaces (Drivers list, Lead Eligibility, Activity Logs, Login History) for non-Workd callers — prevents a tenant admin from accidentally picking us. Turn OFF for internal tenants like workd-sales where Workd staff are the actual operational users. Doesn't affect assigned-entity surfaces (route default driver, trip driver, client salesperson, creator/modifier) — those always show the truth.",
		category: 'Users',
		schema: SanitizeWorkdUsersSchema,
		defaultValue: { enabled: true },
	},
	'imports.max_rows_per_run': {
		label: 'Import Max Rows Per Run',
		description:
			'Maximum data rows a single record-import run may contain (default 10,000). Workd-held governor: geocoding of imported addresses is billed to Workd, so lower this for tenants that overuse large imports instead of relying on uploader restraint.',
		category: 'Lists',
		schema: ImportMaxRowsSchema,
		defaultValue: { maxRows: 10000 },
	},
	'payments.allow_account_funds_deposit': {
		label: 'Allow Account Funds Deposits',
		description:
			'When ON (default), customers may ADD money to their account funds balance — the "Apply to Customer Account" amount on the CRM Make Payment screen and "Apply to My Account" on the storefront. Turn OFF for tenants whose ERP cannot record an on-account deposit (a card processing fee has no invoice to attach to). SPENDING existing account funds is unaffected either way: Account Funds stays selectable as a payment method and still draws the balance down.',
		category: 'Payments',
		schema: AllowAccountFundsDepositSchema,
		defaultValue: { enabled: true },
	},
	'payments.pay_invoices_with_credit_memos': {
		label: 'Pay Invoices With Credit Memos',
		description:
			'When ON, open credit memos (RMAs) appear as credit rows on the Make a Payment screens (CRM and customer portal). Customers apply some or all of a memo’s remaining balance against the invoices they are paying and only the net difference is charged. Requires an ERP that accepts credit-memo applications on exported payments (e.g. HF Foods Workday). OFF by default — PD-15014 removed credit memos from the payable list for every other tenant.',
		category: 'Payments',
		schema: PayInvoicesWithCreditMemosSchema,
		defaultValue: { enabled: false },
	},
	'softphone.twilioSubAccountSid': {
		label: 'Twilio Subaccount SID',
		description:
			'The Twilio subaccount (AC…) that owns this tenant’s softphone numbers. Set during the subaccount migration so the runtime scopes call-control, recording, coaching, and E911 REST operations to the tenant’s own subaccount (isolation). Empty/unset = the tenant still runs on the Twilio master account.',
		category: 'Integrations',
		schema: TwilioSubAccountSidSchema,
		defaultValue: { subAccountSid: '' },
	},
	'softphone.twilioFeatureEnabled': {
		label: 'Twilio Softphone Enabled',
		description:
			'Master on/off kill-switch for the tenant human Twilio softphone (PD-14243). Off by default. Only one input to the derived "on" gate — the softphone is truly on only when this is true AND a subaccount SID + voice key are provisioned AND ≥1 Kari’s Law recipient is configured. Disabling does NOT delete credentials; re-enabling restores. Written by the config-app Enable-Phone flow and the crm-api kill-switch endpoint (same key, no drift).',
		category: 'Integrations',
		schema: TwilioFeatureEnabledSchema,
		defaultValue: { enabled: false },
	},
	'audit.retention': {
		label: 'Audit Log Retention',
		description:
			'Retention windows (days) for the _request and _audit log tables, enforced by the nightly per-tenant retention sweep. Default 90 days each. Raise for tenants needing a longer audit trail (regulatory); config-app-only — no CRM surface.',
		category: 'System',
		schema: AuditRetentionSchema,
		defaultValue: { requestDays: 90, auditDays: 90 },
	},
	'integrations.record_mastering': {
		label: 'Record Mastering',
		description:
			"Per-entity source of truth for externally-linked records. For each entity (Client, Item, Vendor, License) choose whether the external system is master (default — external-id records are locked read-only in the CRM) or Workd is master (records stay editable and sync out). Also overrides which standard/custom fields are locked. Developer/admin-facing — mis-setting this only removes the edit lock; it does NOT change sync direction, so set 'Workd' only for entities whose integration pushes changes OUT. Default (empty) = today's behavior for every tenant.",
		category: 'Integrations',
		schema: RecordMasteringSchema,
		defaultValue: {},
	},
} as const;

/**
 * Infer TypeScript types from schemas
 */
export type AccessLoggingValue = z.infer<typeof AccessLoggingSchema>;
export type SystemAgentEnabledValue = z.infer<typeof SystemAgentEnabledSchema>;
export type ConversationAgentCreditsValue = z.infer<
	typeof ConversationAgentCreditsSchema
>;
export type ThrottleConcurrencyValue = z.infer<
	typeof ThrottleConcurrencySchema
>;
export type RingCentralSettingsValue = z.infer<
	typeof RingCentralSettingsSchema
>;
export type PdfSettingsValue = z.infer<typeof PdfSettingsSchema>;
export type InvoicePdfSettingsValue = z.infer<typeof InvoicePdfSettingsSchema>;
export type PharmacyComplianceValue = z.infer<typeof PharmacyComplianceSchema>;
export type OrderLicenseCheckValue = z.infer<typeof OrderLicenseCheckSchema>;
export type ErpIntegrationValue = z.infer<typeof ErpIntegrationSchema>;
export type PharmacySalesValue = z.infer<typeof PharmacySalesSchema>;
export type BrandAttributionValue = z.infer<typeof BrandAttributionSchema>;
export type ItemIntelligenceValue = z.infer<typeof ItemIntelligenceSchema>;
export type SanitizeWorkdUsersValue = z.infer<typeof SanitizeWorkdUsersSchema>;
export type ImportMaxRowsValue = z.infer<typeof ImportMaxRowsSchema>;
export type AllowAccountFundsDepositValue = z.infer<
	typeof AllowAccountFundsDepositSchema
>;
export type PayInvoicesWithCreditMemosValue = z.infer<
	typeof PayInvoicesWithCreditMemosSchema
>;
export type EmailDomainSendersValue = z.infer<typeof EmailDomainSendersSchema>;
/** A single domain's configured default sender. */
export type EmailDomainSenderEntry = EmailDomainSendersValue['domains'][string];
export type CrossAccountDeliveryValue = z.infer<
	typeof CrossAccountDeliverySchema
>;
export type SensorDiscoveryPatternsValue = z.infer<
	typeof SensorDiscoveryPatternsSchema
>;
export type TwilioSubAccountSidValue = z.infer<
	typeof TwilioSubAccountSidSchema
>;
export type DefaultSubmissionStatusValue = z.infer<
	typeof DefaultSubmissionStatusSchema
>;
export type ClientDefaultStateValue = z.infer<typeof ClientDefaultStateSchema>;
export type RecordMasteringValue = z.infer<typeof RecordMasteringSchema>;
export type AuditRetentionValue = z.infer<typeof AuditRetentionSchema>;

/**
 * Generic type helper for type-safe setting access
 */
export type InstanceSettingValue<
	K extends keyof typeof INSTANCE_SETTINGS_REGISTRY,
> = z.infer<(typeof INSTANCE_SETTINGS_REGISTRY)[K]['schema']>;

/**
 * Helper to get all setting keys (for UI dropdown)
 */
export type InstanceSettingKey = keyof typeof INSTANCE_SETTINGS_REGISTRY;

/**
 * Setting keys that are safe to expose to unauthenticated / pre-login
 * frontends (login screen, public storefront landing). Computed from the
 * `isPublic: true` flag in the registry so adding a new public setting
 * is one line in the registry, not two places.
 *
 * Server endpoints that surface public settings (the bootstrap response
 * served to anonymous visitors) MUST filter against this list. Anything
 * not present here is server-only.
 */
export const PUBLIC_INSTANCE_SETTING_KEYS: ReadonlyArray<InstanceSettingKey> =
	Object.entries(INSTANCE_SETTINGS_REGISTRY)
		.filter(([, cfg]) => (cfg as { isPublic?: boolean }).isPublic === true)
		.map(([key]) => key as InstanceSettingKey);
